<template>
    <div class="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-rose-100 to-rose-200">
        <div class="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
            <div class="text-center mb-8">
                <div
                    class="w-20 h-20 bg-gradient-to-br from-rose-700 to-rose-900 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i class="pi pi-user text-white text-4xl"></i>
                </div>
                <h1 class="text-3xl font-bold text-gray-800">Buletin Ecografie</h1>
                <p class="text-gray-600 mt-2">Autentificare medic</p>
            </div>

            <form @submit.prevent="handleLogin" class="space-y-6">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Email
                    </label>
                    <InputText data-cy="email-input" v-model="email" type="email" placeholder="doctor@clinica.ro" class="w-full" required
                        :class="{ 'p-invalid': loginError }" />
                    <small v-if="emailValidation.errorMessage" class="text-red-600">
                        {{ emailValidation.errorMessage }}
                    </small>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Parolă
                    </label>
                    <div class="relative">
                        <InputText data-cy="password-input" v-model="password" :type="showPassword ? 'text' : 'password'"
                            placeholder="Introdu parola" class="w-full pr-10" required
                            :class="{ 'p-invalid': loginError }" />
                        <button type="button" @click="showPassword = !showPassword"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                            <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                        </button>
                    </div>
                </div>

                <div v-if="loginError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-exclamation-circle"></i>
                        <span>{{ loginError }}</span>
                    </div>
                </div>

                <Button data-cy="login-button" type="submit" label="Autentificare" icon="pi pi-sign-in" :loading="loginLoading"
                    class="w-full bg-gradient-to-r from-rose-700 to-rose-900" severity="danger" />
            </form>

            <div class="mt-6 pt-6 border-t border-gray-200">
                <p class="text-center text-sm text-gray-600">
                    Nu ai cont?
                    <button data-cy="toggle-register-button" @click="showRegister = !showRegister"
                        class="ml-2 text-rose-700 hover:text-rose-900 font-medium">
                        {{ showRegister ? "Autentificare" : "Înregistrează-te" }}
                    </button>
                </p>
            </div>

            <!-- Register Form -->
            <div v-if="showRegister" class="mt-6 pt-6 border-t border-gray-200">
                <h2 class="text-xl font-semibold mb-4 text-center">
                    Înregistrare
                </h2>
                <form @submit.prevent="handleRegister" class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Nume complet
                        </label>
                        <InputText data-cy="name-input" v-model="registerData.name" placeholder="Dr. Ion Popescu" class="w-full" required />
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Specialitate
                        </label>
                        <InputText data-cy="speciality-input" v-model="registerData.speciality" placeholder="Cardiolog" class="w-full" required />
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            E-mail
                        </label>
                        <InputText data-cy="email-register-input" v-model="registerData.email" type="email" placeholder="doctor@clinica.ro"
                            class="w-full" required />
                        <small v-if="registerEmailValidation.errorMessage" class="text-red-600">
                            {{ registerEmailValidation.errorMessage }}
                        </small>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Parolă
                        </label>
                        <div class="relative mb-2">
                            <InputText data-cy="password-register-input" v-model="registerData.password"
                                :type="showRegisterPassword ? 'text' : 'password'" placeholder="Alege o parolă"
                                class="w-full pr-10" required
                                :class="{ 'p-invalid': registerData.password && !passwordValidation.isValid }" />
                            <button data-cy="toggle-register-password-button" type="button" @click="showRegisterPassword = !showRegisterPassword"
                                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                                <i :class="showRegisterPassword ? 'pi pi-eye-slash' : 'pi pi-eye'
                                    "></i>
                            </button>
                        </div>
                        <div v-if="registerError" data-cy="register-error"
                            class="bg-red-50 border border-red-200 text-red-700 px-4 py-1 rounded">
                            <div class="flex items-center gap-3">
                                <i class="pi pi-exclamation-circle"></i>
                                <span>{{ registerError }}</span>
                            </div>
                        </div>

                        <!-- Password validation hints -->
                        <div v-if="registerData.password" class="mt-2 space-y-1 text-xs">
                            <div class="flex items-center gap-2"
                                :class="passwordValidation.validations.value.hasMinLength ? 'text-green-600' : 'text-gray-500'">
                                <i
                                    :class="passwordValidation.validations.value.hasMinLength ? 'pi pi-check-circle' : 'pi pi-circle'"></i>
                                <span>Minimum 8 caractere</span>
                            </div>
                            <div class="flex items-center gap-2"
                                :class="passwordValidation.validations.value.hasUppercase ? 'text-green-600' : 'text-gray-500'">
                                <i
                                    :class="passwordValidation.validations.value.hasUppercase ? 'pi pi-check-circle' : 'pi pi-circle'"></i>
                                <span>Cel puțin o literă mare</span>
                            </div>
                            <div class="flex items-center gap-2"
                                :class="passwordValidation.validations.value.hasLowercase ? 'text-green-600' : 'text-gray-500'">
                                <i
                                    :class="passwordValidation.validations.value.hasLowercase ? 'pi pi-check-circle' : 'pi pi-circle'"></i>
                                <span>Cel puțin o literă mică</span>
                            </div>
                            <div class="flex items-center gap-2"
                                :class="passwordValidation.validations.value.hasNumber ? 'text-green-600' : 'text-gray-500'">
                                <i
                                    :class="passwordValidation.validations.value.hasNumber ? 'pi pi-check-circle' : 'pi pi-circle'"></i>
                                <span>Cel puțin o cifră</span>
                            </div>
                            <div class="flex items-center gap-2"
                                :class="passwordValidation.validations.value.hasSpecialChar ? 'text-green-600' : 'text-gray-500'">
                                <i
                                    :class="passwordValidation.validations.value.hasSpecialChar ? 'pi pi-check-circle' : 'pi pi-circle'"></i>
                                <span>Cel puțin un caracter special (!@#$%^&*)</span>
                            </div>
                        </div>
                    </div>

                    <Button data-cy="register-button" type="submit" label="Creează cont" icon="pi pi-user-plus" :loading="registerLoading"
                        :disabled="registerData.password && !passwordValidation.isValid" class="w-full"
                        severity="success" />
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/config.js";
import { usePasswordValidation } from "../../composables/auth/usePasswordValidation.js";
import { useEmailValidation } from "../../composables/auth/useEmailValidation.js";

const emit = defineEmits(["authenticated"]);

const email = ref("");
const password = ref("");
const showPassword = ref(false);
const showRegister = ref(false);
const showRegisterPassword = ref(false);

const loginError = ref("");
const registerError = ref("");

const loginLoading = ref(false);
const registerLoading = ref(false);

const registerData = ref({
    name: "",
    speciality: "",
    email: "",
    password: "",
});

const registerEmailValidation = useEmailValidation(
    computed(() => registerData.value.email),
    { required: true }
);

// Password validation
const passwordValidation = usePasswordValidation(computed(() => registerData.value.password));
const emailValidation = useEmailValidation(email, { required: true });

const handleLogin = async () => {
    loginLoading.value = true;
    loginError.value = "";

    if (!emailValidation.isValid.value) {
        loginError.value = emailValidation.errorMessage.value;
        return;
    }

    try {
        const userCredential = await signInWithEmailAndPassword(
            auth,
            email.value,
            password.value
        );
        emit("authenticated", userCredential.user);
    } catch (err) {
        console.error("Login error:", err);
        if (err.code === "auth/user-not-found") {
            loginError.value = "Nu există cont cu acest email";
        } else if (err.code === "auth/wrong-password") {
            loginError.value = "Parolă incorectă";
        } else if (err.code === "auth/invalid-credential") {
            loginError.value = "Email sau parolă incorectă";
        } else {
            loginError.value = "Eroare la autentificare: " + err.message;
        }
    } finally {
        loginLoading.value = false;
    }
};

const handleRegister = async () => {
    // Check if password is valid
    if (!passwordValidation.isValid.value) {
        registerError.value = "Parola nu îndeplinește toate cerințele de securitate";
        return;
    }

    if (!registerEmailValidation.isValid.value) {
        registerError.value = registerEmailValidation.errorMessage.value;
        return;
    }

    registerLoading.value = true;
    registerError.value = "";

    try {
        // Create Firebase Auth user
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            registerData.value.email,
            registerData.value.password
        );

        // Save doctor profile to Firestore
        await setDoc(doc(db, "users", userCredential.user.uid), {
            email: registerData.value.email,
            name: registerData.value.name,
            speciality: registerData.value.speciality,
            role: "doctor",
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });

        emit("authenticated", userCredential.user);
    } catch (err) {
        console.error("Registration error:", err);
        if (err.code === "auth/email-already-in-use") {
            registerError.value = "Acest email este deja înregistrat";
        } else if (err.code === "auth/weak-password") {
            registerError.value = "Parola trebuie să aibă minimum 6 caractere";
        } else {
            registerError.value = "Eroare la înregistrare: " + err.message;
        }
    } finally {
        registerLoading.value = false;
    }
};

watch(showRegister, (v) => {
    if (v) loginError.value = "";
    else registerError.value = "";
});
</script>
